import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { User as UserIcon } from "lucide-react";
import { PageHeader } from "./Sugar";

export default function Profile() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || "", gender: user.gender || "", date_of_birth: user.date_of_birth || "",
        height_cm: user.height_cm || "", weight_kg: user.weight_kg || "", waist_cm: user.waist_cm || "",
        blood_group: user.blood_group || "", mobile: user.mobile || "", emergency_contact: user.emergency_contact || "",
        diabetes: !!user.diabetes, hypertension: !!user.hypertension, cholesterol: !!user.cholesterol,
        heart_disease: !!user.heart_disease, thyroid: !!user.thyroid,
        allergies: user.allergies || "", current_medications: user.current_medications || "", family_history: user.family_history || "",
      });
    }
  }, [user]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    setError(""); setMsg(""); setLoading(true);
    try {
      const payload = { ...form,
        height_cm: form.height_cm ? Number(form.height_cm) : undefined,
        weight_kg: form.weight_kg ? Number(form.weight_kg) : undefined,
        waist_cm: form.waist_cm ? Number(form.waist_cm) : undefined,
      };
      Object.keys(payload).forEach(k => payload[k] === "" && delete payload[k]);
      await api.put("/profile", payload); await refresh(); setMsg("Profile updated");
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };

  if (!user) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-4 md:p-8 max-w-3xl mx-auto" data-testid="profile-page">
      <PageHeader icon={UserIcon} title="Profile" subtitle="Personal & medical details" accent="blue" />

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
        <h3 className="font-heading font-semibold text-slate-800 mb-4">Personal information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name"><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-name" /></Field>
          <Field label="Email"><Input value={user.email} disabled className="rounded-xl bg-slate-100 text-slate-500" /></Field>
          <Field label="Gender">
            <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
              <SelectTrigger className="rounded-xl bg-slate-50" data-testid="pf-gender"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem><SelectItem value="female">Female</SelectItem><SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Date of birth"><Input type="date" value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-dob" /></Field>
          <Field label="Height (cm)"><Input type="number" value={form.height_cm} onChange={(e) => set("height_cm", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-height" /></Field>
          <Field label="Weight (kg)"><Input type="number" value={form.weight_kg} onChange={(e) => set("weight_kg", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-weight" /></Field>
          <Field label="Waist (cm)"><Input type="number" value={form.waist_cm} onChange={(e) => set("waist_cm", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-waist" /></Field>
          <Field label="Blood group">
            <Select value={form.blood_group} onValueChange={(v) => set("blood_group", v)}>
              <SelectTrigger className="rounded-xl bg-slate-50" data-testid="pf-blood"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{["A+","A-","B+","B-","AB+","AB-","O+","O-"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Mobile"><Input value={form.mobile} onChange={(e) => set("mobile", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-mobile" /></Field>
          <Field label="Emergency contact"><Input value={form.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-emergency" /></Field>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-5 mb-6">
        <h3 className="font-heading font-semibold text-slate-800 mb-4">Medical information</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          {[
            { k: "diabetes", l: "Diabetes" }, { k: "hypertension", l: "Hypertension" },
            { k: "cholesterol", l: "Cholesterol" }, { k: "heart_disease", l: "Heart disease" },
            { k: "thyroid", l: "Thyroid" },
          ].map(({ k, l }) => (
            <div key={k} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
              <span className="text-sm font-medium text-slate-800">{l}</span>
              <Switch checked={!!form[k]} onCheckedChange={(v) => set(k, v)} data-testid={`pf-${k}`} />
            </div>
          ))}
        </div>
        <Field label="Allergies"><Textarea value={form.allergies} onChange={(e) => set("allergies", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-allergies" /></Field>
        <div className="h-3" />
        <Field label="Current medications"><Textarea value={form.current_medications} onChange={(e) => set("current_medications", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-current-meds" /></Field>
        <div className="h-3" />
        <Field label="Family history"><Textarea value={form.family_history} onChange={(e) => set("family_history", e.target.value)} className="rounded-xl bg-slate-50" data-testid="pf-family" /></Field>
      </div>

      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2 mb-3" data-testid="pf-success">{msg}</div>}
      {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 mb-3">{error}</div>}

      <div className="flex justify-end">
        <Button onClick={save} disabled={loading} className="rounded-full bg-blue-600 hover:bg-blue-700 h-11 px-6" data-testid="pf-save">{loading ? "Saving…" : "Save changes"}</Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
